// @ts-nocheck
import { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useRole } from '@/contexts/RoleContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from '@/components/ui/table';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { MoreHorizontal, Plus } from 'lucide-react';
import { toast } from 'sonner';
import InviteAdminDialog from '@/components/admin/InviteAdminDialog';

const ROLE_BADGE: Record<string, { label: string; cls: string }> = {
  rlevents_super_admin: { label: 'Super Admin', cls: 'bg-purple-100 text-purple-800 border-purple-300' },
  rlevents_tenant_admin: { label: 'Org Admin', cls: 'bg-blue-100 text-blue-800 border-blue-300' },
  rlevents_tenant_staff: { label: 'Staff', cls: 'bg-gray-100 text-gray-800 border-gray-300' },
};

const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  pending: { label: 'Pending', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  accepted: { label: 'Accepted', cls: 'bg-green-100 text-green-800 border-green-300' },
  expired: { label: 'Expired', cls: 'bg-gray-100 text-gray-700 border-gray-300' },
  revoked: { label: 'Revoked', cls: 'bg-red-100 text-red-800 border-red-300' },
};

interface Props {
  scopeOrganizationId?: string; // when set, page filters to that org
  callerIsSuperAdmin: boolean;
  title?: string;
  subtitle?: string;
}

export function AdminInvitationsView({
  scopeOrganizationId,
  callerIsSuperAdmin,
  title = 'Admin invitations',
  subtitle = 'Invite super admins, organization admins, and staff.',
}: Props) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'all' | 'pending' | 'accepted' | 'expired' | 'revoked'>('all');
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data: invites = [], isLoading } = useQuery({
    queryKey: ['admin-invitations', scopeOrganizationId ?? 'all'],
    queryFn: async () => {
      let q = (supabase as any)
        .from('admin_invitations')
        .select('*, organization:organizations(name)')
        .order('created_at', { ascending: false });
      if (scopeOrganizationId) q = q.eq('organization_id', scopeOrganizationId);
      const { data } = await q;
      return data ?? [];
    },
  });

  const filtered = useMemo(() => {
    const now = Date.now();
    return invites.filter((inv: any) => {
      let status = inv.status;
      if (status === 'pending' && new Date(inv.expires_at).getTime() < now) status = 'expired';
      if (tab === 'all') return true;
      return status === tab;
    });
  }, [invites, tab]);

  const handleRevoke = async (id: string) => {
    const { error } = await (supabase as any)
      .from('admin_invitations')
      .update({ status: 'revoked', revoked_at: new Date().toISOString() })
      .eq('id', id);
    if (error) {
      toast.error(error.message);
    } else {
      toast.success('Invitation revoked');
      qc.invalidateQueries({ queryKey: ['admin-invitations'] });
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">{title}</h1>
          <p className="text-muted-foreground text-sm mt-1">{subtitle}</p>
        </div>
        <Button onClick={() => setDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" /> Invite admin
        </Button>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as any)} className="mb-4">
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="accepted">Accepted</TabsTrigger>
          <TabsTrigger value="expired">Expired</TabsTrigger>
          <TabsTrigger value="revoked">Revoked</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Organization</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Invited</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No invitations</TableCell></TableRow>
              ) : (
                filtered.map((inv: any) => {
                  const isExpired = inv.status === 'pending' && new Date(inv.expires_at).getTime() < Date.now();
                  const status = isExpired ? 'expired' : inv.status;
                  const sb = STATUS_BADGE[status] ?? STATUS_BADGE.pending;
                  const rb = ROLE_BADGE[inv.role] ?? { label: inv.role, cls: '' };
                  return (
                    <TableRow key={inv.id}>
                      <TableCell className="font-medium">{inv.invitee_email}</TableCell>
                      <TableCell>{inv.invitee_name || '—'}</TableCell>
                      <TableCell><Badge variant="outline" className={rb.cls}>{rb.label}</Badge></TableCell>
                      <TableCell>{inv.organization?.name || '—'}</TableCell>
                      <TableCell><Badge variant="outline" className={sb.cls}>{sb.label}</Badge></TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {new Date(inv.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {new Date(inv.expires_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="ghost"><MoreHorizontal className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => toast.info('Link is only retrievable at creation time')}
                            >
                              Copy invite link
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => toast.info('Resend coming soon')}>
                              Resend email
                            </DropdownMenuItem>
                            {inv.status === 'pending' && !isExpired && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <DropdownMenuItem
                                    onSelect={(e) => e.preventDefault()}
                                    className="text-destructive"
                                  >
                                    Revoke
                                  </DropdownMenuItem>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Revoke invitation?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This will invalidate the invitation link sent to {inv.invitee_email}.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => handleRevoke(inv.id)}>
                                      Revoke
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <InviteAdminDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        callerIsSuperAdmin={callerIsSuperAdmin}
        defaultOrganizationId={scopeOrganizationId}
        onInvited={() => qc.invalidateQueries({ queryKey: ['admin-invitations'] })}
      />
    </div>
  );
}

export default function AdminInvitationsPage() {
  const { isSuperAdmin } = useRole();
  return <AdminInvitationsView callerIsSuperAdmin={isSuperAdmin} />;
}
